import { X, Siren, Loader2, Mail, User, Building2, AlertTriangle, Send } from 'lucide-react';
import { useSavAccessEmailPreview, useSendSavAccessEmail } from '@/features/contracts/hooks/useContracts';

interface SendSavAccessEmailModalProps {
  contractId: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Aperçu + envoi de l'email « infos de connexion pour déclarer une panne ».
 * Le contenu est généré par l'API (co-brandé garant + Affinicar) : l'aperçu
 * est exactement l'email qui sera envoyé.
 */
export function SendSavAccessEmailModal({ contractId, isOpen, onClose }: SendSavAccessEmailModalProps) {
  const { data: preview, isLoading, error } = useSavAccessEmailPreview(contractId, isOpen);
  const { mutate, isLoading: isSending } = useSendSavAccessEmail();

  if (!isOpen) return null;

  const canSend = !!preview && !preview.blockingReason && !isSending;

  const handleSend = () => {
    if (!canSend) return;
    mutate(contractId, { onSuccess: onClose });
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-3xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Siren className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
              Envoyer les infos au client pour déclarer une panne
            </h3>
          </div>
          <button onClick={onClose} disabled={isSending} className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors" title="Fermer">
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-p-10 tw-text-sm tw-text-gray-500">
            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            Préparation de l'aperçu...
          </div>
        )}

        {!!error && !isLoading && (
          <div className="tw-m-5 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-text-sm tw-text-red-700">
            Impossible de générer l'aperçu de l'email.
          </div>
        )}

        {preview && !isLoading && (
          <>
            {/* Destinataire — bien visible */}
            <div className="tw-px-5 tw-py-4 tw-bg-blue-50 tw-border-b tw-border-blue-100">
              <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-blue-700 tw-m-0 tw-mb-2">
                Destinataire
              </p>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2">
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900">
                  <User className="tw-w-4 tw-h-4 tw-text-blue-600" />
                  {preview.recipient.name || '—'}
                </span>
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900">
                  <Mail className="tw-w-4 tw-h-4 tw-text-blue-600" />
                  {preview.recipient.email || <span className="tw-text-red-600">Aucun email</span>}
                </span>
              </div>
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-m-0 tw-mt-2">
                <Building2 className="tw-w-3.5 tw-h-3.5" />
                Garant : <strong>{preview.guarantor.name}</strong>
              </p>
            </div>

            {preview.blockingReason && (
              <div className="tw-mx-5 tw-mt-4 tw-p-3 tw-flex tw-items-center tw-gap-2 tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-md tw-text-sm tw-text-orange-700">
                <AlertTriangle className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
                {preview.blockingReason}
              </div>
            )}

            {/* Aperçu de l'email */}
            <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col tw-p-5 tw-min-h-0">
              <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-mb-2">
                Objet : <strong className="tw-text-gray-900">{preview.subject}</strong>
              </p>
              <iframe
                title="Aperçu de l'email"
                srcDoc={preview.htmlContent}
                sandbox=""
                className="tw-w-full tw-flex-1 tw-min-h-[420px] tw-border tw-border-gray-200 tw-rounded-md tw-bg-gray-50"
              />
            </div>
          </>
        )}

        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200">
          <button onClick={onClose} disabled={isSending} className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
            Annuler
          </button>
          <button onClick={handleSend} disabled={!canSend} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
            {isSending ? <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" /> : <Send className="tw-w-4 tw-h-4" />}
            Envoyer l'email
          </button>
        </div>
      </div>
    </div>
  );
}
